// Guided intake wizard copy — Swedish (default) + English.
// Keep keys flat; steps array indexed 1..7.

const sv = {
  title: 'Ny användningsfall',
  subtitle: 'AI-guidad ansökan för affärsanvändningsfall',
  continueDraft: 'Fortsätt där du slutade',
  discardDraft: 'Börja om',
  back: 'Tillbaka',
  next: 'Nästa',
  submit: 'Skicka in',
  submitting: 'Skickar in…',
  refine: 'Finslipa med AI',
  refining: 'Tänker…',
  aiAdvisory: 'AI-förslag — vägledande, ej blockerande',
  requiredHint: 'Måste fyllas i för att gå vidare',
  summaryTitle: 'Sammanfattning',
  progressStep: (n, total) => `Steg ${n} av ${total}`,
  confirmationTitle: 'Tack — ditt ärende är inskickat',
  confirmationLink: 'Öppna ärendet',
  confirmationPriority: 'Prioritet',
  confirmationSummary: 'AI-sammanfattning',
  priorityLabel: {
    fast_track: 'Snabbspår',
    standard: 'Standard',
    needs_refinement: 'Behöver förfinas',
  },
  steps: [
    null, // index 0 unused — steps are 1-indexed
    {
      name: 'Problem',
      heading: 'Vilket problem vill du lösa?',
      placeholder: 'Beskriv problemet med dina egna ord. Minst 50 tecken.',
      minChars: 50,
    },
    {
      name: 'Användare',
      heading: 'Vem använder det här idag?',
      description: 'Beskriv målgruppen, hur många de är och hur ofta.',
      fields: {
        description: 'Vem påverkas?',
        count: 'Antal personer',
        frequency: 'Hur ofta?',
        frequencyOptions: { daily: 'Dagligen', weekly: 'Veckovis', monthly: 'Månadsvis' },
      },
    },
    {
      name: 'Nuvarande process',
      heading: 'Hur görs det idag?',
      fields: {
        description: 'Beskriv nuvarande process',
        minutes_per_task: 'Minuter per tillfälle',
        volume_per_week: 'Antal gånger per vecka',
      },
      computedLabel: 'Uppskattad kostnad per år',
      computedUnit: 'timmar',
    },
    {
      name: 'Framgång',
      heading: 'Hur ser framgång ut om 6 månader?',
      placeholder: 'Beskriv önskat utfall.',
      kpiHeading: 'Nyckeltal (välj en eller flera)',
      kpiOptions: {
        time_saved: 'Tid sparad',
        error_reduction: 'Färre fel',
        cost_reduction: 'Lägre kostnad',
        user_satisfaction: 'Nöjdhet',
        throughput: 'Genomströmning',
      },
    },
    {
      name: 'Data & integritet',
      heading: 'Vilken data berörs?',
      typesHeading: 'Datatyper (välj alla som gäller)',
      typeOptions: {
        personal_data: 'Personuppgifter',
        patient_data: 'Patientdata',
        financial: 'Finansiell data',
        internal_docs: 'Interna dokument',
        public_data: 'Öppen/publik data',
      },
      descPlaceholder: 'Kort beskrivning av data som används.',
      sensitivityLabel: 'AI-bedömd känslighetsnivå',
    },
    {
      name: 'Alternativ',
      heading: 'Har du övervägt andra lösningar?',
      placeholder: 'Valfritt — lista befintliga verktyg eller tillvägagångssätt du redan tittat på.',
      aiSuggestLabel: 'Förslag på alternativ från AI',
    },
    {
      name: 'Granska & skicka',
      heading: 'Granska din ansökan',
      generateButton: 'Generera AI-sammanfattning',
      generating: 'Analyserar…',
      impactLabel: 'Effekt (1–5)',
      complexityLabel: 'Komplexitet (1–5)',
      priorityLabel: 'Prioritet',
      summaryLabel: 'AI-sammanfattning',
    },
  ],
};

const en = {
  title: 'New Use Case',
  subtitle: 'AI-guided intake for a business use case',
  continueDraft: 'Continue where you left off',
  discardDraft: 'Start over',
  back: 'Back',
  next: 'Next',
  submit: 'Submit',
  submitting: 'Submitting…',
  refine: 'Refine with AI',
  refining: 'Thinking…',
  aiAdvisory: 'AI suggestion — advisory, never blocks',
  requiredHint: 'Required to continue',
  summaryTitle: 'Summary',
  progressStep: (n, total) => `Step ${n} of ${total}`,
  confirmationTitle: 'Thanks — your request was submitted',
  confirmationLink: 'Open the request',
  confirmationPriority: 'Priority',
  confirmationSummary: 'AI summary',
  priorityLabel: {
    fast_track: 'Fast Track',
    standard: 'Standard',
    needs_refinement: 'Needs Refinement',
  },
  steps: [
    null,
    {
      name: 'Problem',
      heading: 'What problem are you trying to solve?',
      placeholder: 'Describe the problem in your own words. Minimum 50 characters.',
      minChars: 50,
    },
    {
      name: 'Users',
      heading: 'Who uses this today?',
      description: 'Describe the target users, roughly how many, and how often.',
      fields: {
        description: 'Who is affected?',
        count: 'Number of people',
        frequency: 'How often?',
        frequencyOptions: { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' },
      },
    },
    {
      name: 'Current process',
      heading: 'How is this done today?',
      fields: {
        description: 'Describe the current process',
        minutes_per_task: 'Minutes per task',
        volume_per_week: 'Volume per week',
      },
      computedLabel: 'Estimated cost per year',
      computedUnit: 'hours',
    },
    {
      name: 'Success',
      heading: 'What does success look like in 6 months?',
      placeholder: 'Describe the desired outcome.',
      kpiHeading: 'KPIs (pick one or more)',
      kpiOptions: {
        time_saved: 'Time saved',
        error_reduction: 'Error reduction',
        cost_reduction: 'Cost reduction',
        user_satisfaction: 'User satisfaction',
        throughput: 'Throughput',
      },
    },
    {
      name: 'Data & privacy',
      heading: 'What data does this touch?',
      typesHeading: 'Data types (select all that apply)',
      typeOptions: {
        personal_data: 'Personal data',
        patient_data: 'Patient data',
        financial: 'Financial data',
        internal_docs: 'Internal documents',
        public_data: 'Public / open data',
      },
      descPlaceholder: 'Brief description of the data involved.',
      sensitivityLabel: 'AI-assessed sensitivity tier',
    },
    {
      name: 'Alternatives',
      heading: 'Have you considered existing tools or approaches?',
      placeholder: 'Optional — list tools or approaches you already looked at.',
      aiSuggestLabel: 'AI-suggested alternatives',
    },
    {
      name: 'Review & submit',
      heading: 'Review your submission',
      generateButton: 'Generate AI summary',
      generating: 'Analysing…',
      impactLabel: 'Impact (1–5)',
      complexityLabel: 'Complexity (1–5)',
      priorityLabel: 'Priority',
      summaryLabel: 'AI summary',
    },
  ],
};

const TRANSLATIONS = { sv, en };

export function getT(lang) {
  return TRANSLATIONS[lang] || TRANSLATIONS.sv;
}

export const SUPPORTED_LANGS = ['sv', 'en'];
export const DEFAULT_LANG = 'sv';
